/**
 * Round-end reveal scene. Shows the round result (WIN or TIMEOUT), the
 * winners list with points, and the full board with all symbols revealed.
 */

import type { RevealState } from '../state/types.js';

export interface RevealHandle {
  setVisible(v: boolean): void;
  setReveal(r: RevealState): void;
  dispose(): void;
}

const ROOT_ID = 'cm-reveal';

export function createReveal(stage: HTMLElement): RevealHandle {
  ensureStyles();
  const root = document.createElement('div');
  root.id = ROOT_ID;
  root.setAttribute('data-overlay', 'reveal');
  root.innerHTML = `
    <div class="cm-reveal-grid">
      <header class="cm-reveal-header">
        <h1 class="cm-reveal-title" data-cm-reveal-title>ALL MATCHED</h1>
        <p class="cm-reveal-sub" data-cm-reveal-sub>Round complete</p>
      </header>

      <ol class="cm-reveal-winners" data-cm-reveal-winners></ol>
    </div>
  `;
  stage.appendChild(root);

  function setVisible(v: boolean): void {
    root.style.display = v ? '' : 'none';
  }

  function setReveal(r: RevealState): void {
    const title = root.querySelector('[data-cm-reveal-title]');
    const sub = root.querySelector('[data-cm-reveal-sub]');
    if (title) title.textContent = r.reason === 'all_matched' ? 'ALL MATCHED!' : 'TIMES UP';
    if (sub) {
      sub.textContent =
        r.reason === 'all_matched'
          ? `Round ${r.index + 1} complete`
          : `Round ${r.index + 1} ended`;
    }
    const winners = root.querySelector('[data-cm-reveal-winners]');
    if (winners) {
      if (r.winners.length === 0) {
        winners.innerHTML = `<li class="cm-reveal-empty">No matches this round</li>`;
      } else {
        winners.innerHTML = r.winners
          .slice(0, 5)
          .map(
            (w) => `
              <li class="cm-reveal-row">
                <span class="cm-reveal-rank">${w.rank}</span>
                <span class="cm-reveal-name">${escapeHtml(w.display_name)}</span>
                <span class="cm-reveal-pts">+${w.points}</span>
              </li>
            `,
          )
          .join('');
      }
    }
  }

  return {
    setVisible,
    setReveal,
    dispose() {
      root.remove();
    },
  };
}

function ensureStyles(): void {
  if (document.getElementById('cm-reveal-style')) return;
  const s = document.createElement('style');
  s.id = 'cm-reveal-style';
  s.textContent = `
    #${ROOT_ID} {
      position: absolute;
      inset: 0;
      display: block;
    }
    #${ROOT_ID} .cm-reveal-grid {
      display: grid;
      grid-template-rows: auto 1fr;
      gap: 32px;
      padding: 80px 200px;
      height: 100%;
      box-sizing: border-box;
      align-content: center;
      text-align: center;
    }
    #${ROOT_ID} .cm-reveal-title {
      font-family: 'Bebas Neue', sans-serif;
      font-size: 180px;
      margin: 0;
      letter-spacing: 8px;
      background: linear-gradient(180deg, var(--sg-primary, #A855F7), var(--sg-accent, #EC4899));
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
    }
    #${ROOT_ID} .cm-reveal-sub {
      font-family: 'Inter', sans-serif;
      font-size: 36px;
      color: rgba(255,255,255,0.85);
      margin: 0;
    }
    #${ROOT_ID} .cm-reveal-winners {
      list-style: none;
      padding: 0;
      margin: 40px auto 0;
      max-width: 800px;
      width: 100%;
    }
    #${ROOT_ID} .cm-reveal-row {
      display: grid;
      grid-template-columns: 80px 1fr auto;
      align-items: center;
      gap: 20px;
      padding: 16px 24px;
      border-radius: 12px;
      background: rgba(31, 27, 75, 0.6);
      margin-bottom: 12px;
      font-size: 32px;
    }
    #${ROOT_ID} .cm-reveal-rank {
      font-family: 'Bebas Neue', sans-serif;
      color: var(--sg-secondary, #06B6D4);
      font-size: 40px;
    }
    #${ROOT_ID} .cm-reveal-name {
      color: #fff;
      text-align: left;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    #${ROOT_ID} .cm-reveal-pts {
      color: var(--sg-celebration, #22C55E);
      font-family: 'Inter', sans-serif;
      font-weight: 700;
    }
    #${ROOT_ID} .cm-reveal-empty {
      list-style: none;
      font-size: 28px;
      color: rgba(255,255,255,0.6);
    }
  `;
  document.head.appendChild(s);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
